import type {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
  Router,
} from 'vue-router'
import type { RouteKey } from '@elegant-router/types'
import { useRouteStore } from '@/store/modules/route'

/**
 * create route guard
 *
 * @param router router instance
 */
export function createRouteGuard(router: Router) {
  router.beforeEach(async (to, from, next) => {
    const location = await initRoute(to)

    if (location) {
      next(location)
      return
    }

    // switch route normally
    handleRouteSwitch(to, from, next)
  })

  router.afterEach((to, from, next) => {
    const routeStore = useRouteStore()
    // 设置导航项激活状态
    routeStore.setNavItemActive(to.path)
  })
}

/**
 * initialize route
 *
 * @param to to route
 */
async function initRoute(
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | null> {
  const routeStore = useRouteStore()
  const notFoundRoute: RouteKey = 'not-found'

  // if the constant route is not initialized, then initialize the constant route
  if (!routeStore.isInitConstantRoute) {
    await routeStore.initConstantRoute()

    // after the constant route is initialized, redirect to the original route
    const path = to.fullPath
    const location: RouteLocationRaw = {
      path,
      replace: true,
      query: to.query,
      hash: to.hash,
    }

    return location
  }

  // initialize auth routes if not initialized
  if (!routeStore.isInitAuthRoute) {
    await routeStore.initAuthRoute()
  }

  // if not a not-found route, allow access
  if (to.name !== notFoundRoute) {
    return null
  }

  return null
}

function handleRouteSwitch(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  // route with href
  if (to.meta.href) {
    window.open(to.meta.href as string, '_blank')
    next({
      path: from.fullPath,
      replace: true,
      query: from.query,
      hash: to.hash,
    })
    return
  }

  next()
}
